import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Grid,
  TextField,
  Button,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Autocomplete,
  Chip,
  Alert
} from '@mui/material';
import { Save as SaveIcon, ArrowBack as BackIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../../utils/api';
import { RequiredLabel, getFieldErrorSx, validateForm } from '../../components/common/FormHelpers';
import InternationalPhoneField from '../../components/common/InternationalPhoneField';
import StructuredAddressFields from '../../components/common/StructuredAddressFields';
import {
  CONTACT_TYPE_OPTIONS,
  normalizeContactRoles,
  primaryContactTypeFromRoles,
  effectiveContactRoles
} from './contactRolesUtil';

// Required fields for contact form (contact types validated separately as multi-select)
const REQUIRED_FIELDS = ['first_name', 'last_name', 'email'];

const ContactForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [selectedAccounts, setSelectedAccounts] = useState([]);
  const [workAuthOptions, setWorkAuthOptions] = useState([]);
  const [validationErrors, setValidationErrors] = useState({});
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    mobile: '',
    linkedin_url: '',
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    zip_code: '',
    country: 'US',
    contact_type: 'regular',
    contact_roles: ['regular'],
    status: 'active',
    work_authorization: '',
    skills: '',
    experience_years: '',
    current_salary: '',
    expected_salary: '',
    notes: '',
    source: ''
  });

  useEffect(() => {
    fetchAccounts();
    fetchWorkAuthorizations();
    if (isEdit) {
      fetchContact();
    }
  }, [id]);

  const fetchAccounts = async () => {
    try {
      const response = await api.get('/accounts?limit=100');
      setAccounts(response.data.data || []);
    } catch (error) {
      console.error('Failed to fetch accounts');
    }
  };

  const fetchContact = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/contacts/${id}`);
      const contactData = response.data.data;
      const roles = effectiveContactRoles(contactData);
      setFormData((prev) => ({
        ...prev,
        ...contactData,
        contact_roles: roles,
        contact_type: primaryContactTypeFromRoles(roles)
      }));
      // Set selected accounts from existing associations
      if (contactData.Accounts) {
        setSelectedAccounts(contactData.Accounts);
      }
    } catch (error) {
      toast.error('Failed to fetch contact');
      navigate('/contacts');
    } finally {
      setLoading(false);
    }
  };

  const fetchWorkAuthorizations = async () => {
    try {
      const response = await api.get('/contacts/work-authorizations');
      setWorkAuthOptions(response.data?.data?.options || []);
    } catch (error) {
      console.error('Failed to fetch work authorization options');
      setWorkAuthOptions([]);
    }
  };

  const patchFormData = (patch) => {
    setFormData((prev) => ({ ...prev, ...patch }));
    Object.keys(patch).forEach((name) => {
      if (validationErrors[name]) {
        setValidationErrors((prev) => {
          const next = { ...prev };
          delete next[name];
          return next;
        });
      }
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear validation error when user starts typing
    if (validationErrors[name]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    const { isValid, errors } = validateForm(formData, REQUIRED_FIELDS);
    if (!isValid) {
      setValidationErrors(errors);
      toast.error('Please fill in all required fields marked with *');
      const firstErrorField = REQUIRED_FIELDS.find((field) => errors[field]);
      if (firstErrorField) {
        const element = document.querySelector(`[name="${firstErrorField}"]`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          element.focus();
        }
      }
      return;
    }

    const rolesNorm = normalizeContactRoles(formData.contact_roles, formData.contact_type);
    if (!rolesNorm.length) {
      toast.error('Select at least one contact type.');
      return;
    }

    setSaving(true);

    try {
      const primary = primaryContactTypeFromRoles(rolesNorm);
      const submitData = {
        ...formData,
        contact_roles: rolesNorm,
        contact_type: primary,
        account_ids: selectedAccounts.map((acc) => acc.id),
      };
      if (rolesNorm.includes('employee')) {
        delete submitData.reporting_manager_contact_id;
        delete submitData.alternate_reporting_manager_contact_id;
        delete submitData.external_employee_id;
        delete submitData.employee_job_role_id;
        delete submitData.employee_title_id;
        delete submitData.employee_offered_title_id;
        delete submitData.skip_reporting_manager;
      } else {
        submitData.reporting_manager_contact_id = null;
        submitData.alternate_reporting_manager_contact_id = null;
        submitData.external_employee_id = null;
        submitData.employee_job_role_id = null;
        submitData.employee_title_id = null;
        submitData.employee_offered_title_id = null;
      }
      if (!rolesNorm.includes('candidate')) {
        submitData.work_authorization = null;
      }

      if (isEdit) {
        await api.put(`/contacts/${id}`, submitData);
        toast.success('Contact updated successfully');
      } else {
        await api.post('/contacts', submitData);
        toast.success('Contact created successfully');
      }
      navigate('/contacts');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save contact');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Button
          startIcon={<BackIcon />}
          onClick={() => navigate('/contacts')}
        >
          Back
        </Button>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          {isEdit ? 'Edit Contact' : 'New Contact'}
        </Typography>
      </Box>

      <form onSubmit={handleSubmit}>
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
              Basic Information
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label={<RequiredLabel>First Name</RequiredLabel>}
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleChange}
                  required
                  error={!!validationErrors.first_name}
                  helperText={validationErrors.first_name ? 'First Name is required' : ''}
                  sx={getFieldErrorSx(validationErrors.first_name)}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label={<RequiredLabel>Last Name</RequiredLabel>}
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleChange}
                  required
                  error={!!validationErrors.last_name}
                  helperText={validationErrors.last_name ? 'Last Name is required' : ''}
                  sx={getFieldErrorSx(validationErrors.last_name)}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label={<RequiredLabel>Email</RequiredLabel>}
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  error={!!validationErrors.email}
                  helperText={validationErrors.email ? 'Email is required' : ''}
                  sx={getFieldErrorSx(validationErrors.email)}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <InternationalPhoneField label="Phone" name="phone" value={formData.phone} onChange={handleChange} />
              </Grid>
              <Grid item xs={12} md={4}>
                <InternationalPhoneField label="Mobile" name="mobile" value={formData.mobile} onChange={handleChange} />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="LinkedIn URL"
                  name="linkedin_url"
                  value={formData.linkedin_url}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} md={8}>
                <Autocomplete
                  multiple
                  options={CONTACT_TYPE_OPTIONS}
                  getOptionLabel={(o) => o.label}
                  isOptionEqualToValue={(a, b) => a.code === b.code}
                  value={CONTACT_TYPE_OPTIONS.filter((o) =>
                    normalizeContactRoles(formData.contact_roles, formData.contact_type).includes(o.code)
                  )}
                  onChange={(_, newVal) => {
                    const codes = newVal.map((o) => o.code);
                    const norm = normalizeContactRoles(codes, formData.contact_type);
                    setFormData((prev) => ({
                      ...prev,
                      contact_roles: norm,
                      contact_type: primaryContactTypeFromRoles(norm)
                    }));
                  }}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip label={option.label} {...getTagProps({ index })} key={option.code} size="small" />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={<RequiredLabel>Contact types</RequiredLabel>}
                      placeholder="Select all that apply"
                      helperText="A person can be a candidate, trainee, employee, etc. Primary type for legacy rules is chosen automatically (e.g. Employee overrides Regular)."
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    name="status"
                    value={formData.status}
                    label="Status"
                    onChange={handleChange}
                  >
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="inactive">Inactive</MenuItem>
                    <MenuItem value="blacklisted">Blacklisted</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <Autocomplete
                  multiple
                  options={accounts}
                  getOptionLabel={(option) => option.company_name || ''}
                  value={selectedAccounts}
                  onChange={(event, newValue) => setSelectedAccounts(newValue)}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Associated Accounts (Companies)"
                      placeholder="Select accounts..."
                    />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        label={option.company_name}
                        {...getTagProps({ index })}
                        color="primary"
                        variant="outlined"
                      />
                    ))
                  }
                />
              </Grid>
              {normalizeContactRoles(formData.contact_roles, formData.contact_type).includes('candidate') && (
                <Grid item xs={12} md={4}>
                  <Autocomplete
                    options={workAuthOptions}
                    value={workAuthOptions.find((o) => o.code === formData.work_authorization) || null}
                    onChange={(_, option) =>
                      setFormData((prev) => ({ ...prev, work_authorization: option?.code || '' }))
                    }
                    getOptionLabel={(o) => o.label || ''}
                    isOptionEqualToValue={(a, b) => a.code === b.code}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Candidate Work Authorization"
                        placeholder="Type to search and select"
                      />
                    )}
                  />
                </Grid>
              )}
            </Grid>
          </CardContent>
        </Card>

        {normalizeContactRoles(formData.contact_roles, formData.contact_type).includes('employee') && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Alert severity="info" sx={{ mb: 0 }}>
                Job role, employee level, offered title, reporting and alternate managers, and payroll or external ID are
                captured under <strong>Administration → Employees</strong> when you add or edit the employee HR profile
                (administrators only). Save this contact first, then open Employees to complete that profile.
              </Alert>
            </CardContent>
          </Card>
        )}

        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <StructuredAddressFields
                  values={{
                    country: formData.country,
                    state: formData.state,
                    city: formData.city,
                    zip_code: formData.zip_code,
                    address_line1: formData.address_line1,
                    address_line2: formData.address_line2,
                  }}
                  onPatch={patchFormData}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
              Professional Information
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Skills"
                  name="skills"
                  value={formData.skills}
                  onChange={handleChange}
                  multiline
                  rows={3}
                  placeholder="Enter skills separated by commas"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Experience (Years)"
                  name="experience_years"
                  type="number"
                  value={formData.experience_years}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Current Salary"
                  name="current_salary"
                  type="number"
                  value={formData.current_salary}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Expected Salary"
                  name="expected_salary"
                  type="number"
                  value={formData.expected_salary}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Source"
                  name="source"
                  value={formData.source}
                  onChange={handleChange}
                  placeholder="e.g., LinkedIn, Referral, Job Board"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Notes"
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  multiline
                  rows={4}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <Button
            variant="outlined"
            onClick={() => navigate('/contacts')}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
            disabled={saving}
            sx={{
              background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
            }}
          >
            {saving ? 'Saving...' : 'Save Contact'}
          </Button>
        </Box>
      </form>
    </Box>
  );
};

export default ContactForm;
